// components/source/useBrandMention.tsx
//
// The "@" brand mention, shared by every Source composer (SRC-025 / SRC-028). Typing "@" opens a
// popup fed by the Brand referential, so a brand is named with its canonical spelling instead of
// from memory - store and contact data matches on NAME, so a misspelled brand is a wrong brand.
// Picking inserts "@Name " at the caret as plain text: the composers are textareas, so no rich
// pill, and the agent resolves brands by name.
//
// A HOOK, not a component, because the menu, the caret tracking and the keyboard handling have to
// interleave with each composer's own handlers (slash menu first came, same shape). First shipped
// inside ChatInput only, while the campaign conversation types into ChatTab - the exact "not
// findable where it is asked for" failure the slash menu had (SRC-024).
import React, { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { BrandMark } from '@/components/ui/BrandMark';

type MentionBrand = { id: string; name: string; logoUrl: string | null; onShortlist: boolean };

export function useBrandMention(textareaRef: RefObject<HTMLTextAreaElement | null>, enabled = true): {
  /** Call whenever the caret may have moved: change, keyup, click. */
  refreshMention: () => void;
  /** Returns true when the event was consumed by the mention menu; stop there. */
  handleMentionKeyDown: (event: React.KeyboardEvent<HTMLElement>) => boolean;
  mentionMenu: React.ReactNode;
  /** Close the popup (blur). */
  closeMention: () => void;
} {
  const { kernel } = useKernel();
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null);
  // The "@" position of the last pick (or Enter-to-send). An inserted "@Name " still matches the
  // trigger regex, so without this the menu reopened the instant a brand was picked and swallowed
  // the Enter meant to send the message - the reported "it bugs" (SRC-029). A NEW "@" elsewhere
  // clears it.
  const suppressedStartRef = useRef<number | null>(null);

  const refreshMention = useCallback(() => {
    if (!enabled) {
      setMention(null);
      return;
    }
    const ta = textareaRef.current;
    const caret = ta?.selectionStart ?? 0;
    const before = (ta?.value ?? '').slice(0, caret);
    // "@" at a word start, then up to 40 chars without another "@" or a newline: longer is prose
    // (an email address, a handle pasted mid-sentence), not a mention being typed.
    const match = /(^|[\s(])@([^@\n]{0,40})$/.exec(before);
    const query = match?.[2] ?? '';
    const start = caret - query.length - 1;
    if (!match) {
      suppressedStartRef.current = null;
      setMention(null);
      return;
    }
    if (suppressedStartRef.current === start) {
      setMention(null);
      return;
    }
    if (suppressedStartRef.current !== null) suppressedStartRef.current = null;
    setMention({ query, start });
  }, [enabled, textareaRef]);

  const closeMention = useCallback(() => setMention(null), []);

  const [mentionBrands, setMentionBrands] = useState<MentionBrand[]>([]);
  const mentionSeqRef = useRef(0);
  useEffect(() => {
    if (!mention || !enabled) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- clearing on close must be synchronous or a stale menu flashes
      setMentionBrands([]);
      return;
    }
    const seq = ++mentionSeqRef.current;
    const normalizedQuery = mention.query.toLowerCase().replace(/[^a-z0-9]/g, '');
    const rank = (name: string) => {
      const position = name.toLowerCase().replace(/[^a-z0-9]/g, '').indexOf(normalizedQuery);
      return position === -1 ? 1_000 : position;
    };
    // THE OPEN SHORTLIST COMES FIRST (owner, SRC-029): a bare "@" lists the brands already on the
    // list in front of the user, then typing narrows into the whole referential. Read from the
    // registry the open table hydrated - no fetch, no subscription churn.
    const shortlistBrands = ((): MentionBrand[] => {
      const state = kernel.current?.getState?.();
      const campaignId = state?.sourceSelectedCampaignId;
      if (!campaignId) return [];
      const leadRegistry = (state?.inventory?.Lead?.registry ?? {}) as Record<string, { brandId?: string; brandName?: string | null; brandLogoUrl?: string | null; lastCampaignId?: string | null }>;
      const seen = new Set<string>();
      const brandRegistry = (state?.inventory?.Brand?.registry ?? {}) as Record<string, { logoUrl?: string | null } | undefined>;
      const rows: MentionBrand[] = [];
      for (const row of Object.values(leadRegistry)) {
        if (row?.lastCampaignId !== campaignId || !row.brandId || seen.has(row.brandId)) continue;
        const name = (row.brandName ?? '').trim();
        if (!name) continue;
        seen.add(row.brandId);
        rows.push({ id: row.brandId, name, logoUrl: row.brandLogoUrl ?? brandRegistry[row.brandId]?.logoUrl ?? null, onShortlist: true });
      }
      return rows
        .filter((brand) => !normalizedQuery || rank(brand.name) < 1_000)
        .sort((a, b) => rank(a.name) - rank(b.name) || a.name.localeCompare(b.name));
    })();

    // A bare "@" is answered from the shortlist alone when it has rows; the referential search
    // only runs once the user types (or when no shortlist is open).
    if (!normalizedQuery && shortlistBrands.length > 0) {
      setMentionBrands(shortlistBrands.slice(0, 8));
      return;
    }

    // Debounced live search through the canonical inventory gateway; the registry holds the rows
    // the query hydrated, so names are read back without a second fetch. A wider page than shown,
    // because the server pages alphabetically: the best match for "vand" is "V and B" (normalized
    // prefix), which alphabetical order buries below the laundromats. Rank here and show eight,
    // shortlist matches ahead of referential ones.
    const timer = setTimeout(async () => {
      const result = await kernel.current?.queryInventory('Brand', {
        filters: { search: mention.query },
        limit: 24,
        ttl: 60_000,
      });
      if (mentionSeqRef.current !== seq) return;
      const registry = kernel.current?.getInventory().Brand.registry ?? {};
      const ids: string[] = Array.isArray(result?.ids) ? result.ids : [];
      const referential = ids
        .map((id) => {
          const brand = registry[id] as { name?: string; logoUrl?: string | null } | undefined;
          return { id, name: (brand?.name ?? '').trim(), logoUrl: brand?.logoUrl ?? null, onShortlist: false };
        })
        .filter((brand) => brand.name.length > 0)
        .sort((a, b) => rank(a.name) - rank(b.name) || a.name.length - b.name.length);
      const merged: MentionBrand[] = [];
      const taken = new Set<string>();
      for (const brand of [...shortlistBrands, ...referential]) {
        if (taken.has(brand.id)) continue;
        taken.add(brand.id);
        merged.push(brand);
      }
      setMentionBrands(merged.slice(0, 8));
    }, 200);
    return () => clearTimeout(timer);
  }, [enabled, mention, kernel]);

  // Same derived-highlight pattern as the slash menus: remember which mention the index belongs
  // to instead of resetting state on every keystroke.
  const mentionKey = mention ? `${mention.start}:${mention.query}` : '';
  const [mentionSelection, setMentionSelection] = useState<{ key: string; index: number }>({ key: '', index: 0 });
  const mentionIndex = mentionSelection.key === mentionKey ? mentionSelection.index : 0;

  const pickMentionBrand = useCallback((name: string) => {
    const ta = textareaRef.current;
    if (!ta || !mention) return;
    const caret = ta.selectionStart ?? ta.value.length;
    const next = `${ta.value.slice(0, mention.start)}@${name} ${ta.value.slice(caret)}`;
    kernel.current?.setChatPrompt(next);
    suppressedStartRef.current = mention.start;
    setMention(null);
    const newCaret = mention.start + name.length + 2;
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(newCaret, newCaret); });
  }, [kernel, mention, textareaRef]);

  const handleMentionMouseDown = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const name = event.currentTarget.dataset.mentionName;
    if (name) pickMentionBrand(name);
  }, [pickMentionBrand]);

  const handleMentionMouseEnter = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const index = event.currentTarget.dataset.mentionIndex;
    if (index) setMentionSelection({ key: mentionKey, index: Number(index) });
  }, [mentionKey]);

  const handleMentionKeyDown = useCallback((event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (!mention) return false;
    if (event.key === 'Escape') { event.preventDefault(); setMention(null); return true; }
    if (mentionBrands.length === 0) return false;
    if (event.key === 'ArrowDown') { event.preventDefault(); setMentionSelection({ key: mentionKey, index: (mentionIndex + 1) % mentionBrands.length }); return true; }
    if (event.key === 'ArrowUp') { event.preventDefault(); setMentionSelection({ key: mentionKey, index: (mentionIndex - 1 + mentionBrands.length) % mentionBrands.length }); return true; }
    if (event.key === 'Tab') {
      event.preventDefault();
      const chosen = mentionBrands[Math.min(mentionIndex, mentionBrands.length - 1)];
      if (chosen) pickMentionBrand(chosen.name);
      return true;
    }
    if (event.key === 'Enter') {
      // Enter SENDS (owner, SRC-029): close the menu, remember the position so the keyup does not
      // reopen it, and let the composer's own Enter handler run.
      suppressedStartRef.current = mention.start;
      setMention(null);
      return false;
    }
    return false;
  }, [mention, mentionBrands, mentionIndex, mentionKey, pickMentionBrand]);

  // Same shape as the List/Factory mention menu (useFactoryArchetypeMention): one design for "@".
  const mentionMenu = mention && mentionBrands.length > 0 ? (
    <div role="listbox" aria-label="Brands" className="mb-2 max-h-[min(40vh,320px)] overflow-y-auto custom-scrollbar rounded-2xl border border-white/10 bg-[#2F2F2F] p-2 shadow-xl">
      <div className="px-3 pb-1 pt-2 text-sm text-white/50">Brands</div>
      {mentionBrands.map((brand, index) => (
        <button
          key={brand.id}
          type="button"
          role="option"
          aria-selected={index === mentionIndex}
          data-mention-name={brand.name}
          data-mention-index={index}
          ref={index === mentionIndex ? (el) => el?.scrollIntoView({ block: 'nearest' }) : undefined}
          onMouseDown={handleMentionMouseDown}
          onMouseEnter={handleMentionMouseEnter}
          className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors ${index === mentionIndex ? 'bg-white/10' : 'hover:bg-white/[0.06]'}`}
        >
          <BrandMark logoUrl={brand.logoUrl} name={brand.name} size={32} radius={8} />
          <span className="min-w-0">
            <span className="block truncate text-sm text-[#ECECEC]">@{brand.name}</span>
            <span className="block truncate text-xs text-white/50">{brand.onShortlist ? 'On this shortlist' : 'Brand referential'}</span>
          </span>
        </button>
      ))}
    </div>
  ) : null;

  return { refreshMention, handleMentionKeyDown, mentionMenu, closeMention };
}
